import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { getRequiredDocuments, getOptionalDocuments, DOCUMENT_DEFINITIONS } from '../../../config/tradeRequirements';

interface DocumentsVerificationPanelProps {
  provider: any;
  onUpdate: () => void;
}

export default function DocumentsVerificationPanel({ provider, onUpdate }: DocumentsVerificationPanelProps) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const requiredDocs = getRequiredDocuments(provider.trade_type);
  const optionalDocs = getOptionalDocuments(provider.trade_type);

  useEffect(() => {
    loadDocuments();
  }, [provider.id]);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('provider_documents')
        .select('*')
        .eq('provider_id', provider.id)
        .eq('trade_type', provider.trade_type);

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentStatus = (docKey: string) => {
    const doc = documents.find(d => d.doc_key === docKey);
    return doc || { status: 'not_submitted' };
  };

  const handleFileUpload = async (docKey: string, file: File) => {
    setUploading(docKey);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload to PRIVATE storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${provider.id}/${docKey}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('provider-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('provider-documents')
        .getPublicUrl(fileName);

      // Create or update document record
      const existingDoc = documents.find(d => d.doc_key === docKey);

      if (existingDoc) {
        await supabase
          .from('provider_documents')
          .update({
            file_url: publicUrl,
            status: 'submitted',
            submitted_at: new Date().toISOString()
          })
          .eq('id', existingDoc.id);
      } else {
        await supabase
          .from('provider_documents')
          .insert({
            provider_id: provider.id,
            trade_type: provider.trade_type,
            doc_key: docKey,
            file_url: publicUrl,
            status: 'submitted',
            submitted_at: new Date().toISOString()
          });
      }

      setMessage({ type: 'success', text: 'Document uploaded successfully!' });
      await loadDocuments();
      onUpdate();
    } catch (error: any) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload document' });
    } finally {
      setUploading(null);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const canActivate = () => {
    // Check if all required documents are approved
    const allRequiredApproved = requiredDocs.every(doc => {
      const status = getDocumentStatus(doc.key);
      return status.status === 'approved';
    });

    // Profile photo must be uploaded
    const hasProfilePhoto = !!provider.profile_photo_url;

    return allRequiredApproved && hasProfilePhoto;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {provider.provider_status === 'subscription_paid_pending_verification' && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <i className="ri-time-line text-orange-600 text-2xl"></i>
            <div>
              <h3 className="font-bold text-orange-900 mb-2">Verification Pending</h3>
              <p className="text-sm text-orange-800">
                Please upload all required documents below. Our team will review them within 24-48 hours.
              </p>
            </div>
          </div>
        </div>
      )}

      {provider.provider_status === 'verification_pending' && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <i className="ri-loader-4-line text-blue-600 text-2xl animate-spin"></i>
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Under Review</h3>
              <p className="text-sm text-blue-800">
                Your documents are being reviewed by our team. We'll notify you once the review is complete.
              </p>
            </div>
          </div>
        </div>
      )}

      {provider.provider_status === 'approved_needs_workwear' && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <i className="ri-check-line text-green-600 text-2xl"></i>
            <div>
              <h3 className="font-bold text-green-900 mb-2">Documents Approved!</h3>
              <p className="text-sm text-green-800">
                All your documents have been approved. Please purchase workwear to activate your account.
              </p>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'} border-2 rounded-xl p-4`}>
          <div className="flex items-center gap-2">
            <i className={`${message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-xl`}></i>
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        </div>
      )}

      {/* Profile Photo Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-teal-100">
            {provider.profile_photo_url ? (
              <img src={provider.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <i className="ri-user-line text-gray-400 text-2xl"></i>
              </div>
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900">Profile Photo</h3>
            <p className="text-sm text-gray-600">Shown to customers</p>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-check-circle-fill text-green-500 text-2xl"></i>
            <span className="text-sm font-medium text-green-700">Uploaded</span>
          </div>
        </div>
      </div>

      {/* Required Documents */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Required Documents</h2>
        <div className="space-y-4">
          {requiredDocs.map((doc) => {
            const status = getDocumentStatus(doc.key);
            return (
              <div
                key={doc.key}
                className={`border-2 rounded-lg p-6 ${
                  status.status === 'approved' ? 'border-green-200 bg-green-50' :
                  status.status === 'rejected' ? 'border-red-200 bg-red-50' :
                  status.status === 'submitted' ? 'border-blue-200 bg-blue-50' :
                  'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{doc.label}</h3>
                    <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
                    
                    {status.status === 'not_submitted' && (
                      <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                        Not Submitted
                      </span>
                    )}
                    {status.status === 'submitted' && (
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                        Under Review
                      </span>
                    )}
                    {status.status === 'approved' && (
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                        ✓ Approved
                      </span>
                    )}
                    {status.status === 'rejected' && (
                      <div>
                        <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full mb-2">
                          Rejected
                        </span>
                        {status.rejection_reason && (
                          <p className="text-sm text-red-700 mt-2">
                            <strong>Reason:</strong> {status.rejection_reason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {status.status === 'approved' && (
                    <i className="ri-check-circle-fill text-green-500 text-3xl"></i>
                  )}
                </div>

                {(status.status === 'not_submitted' || status.status === 'rejected') && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(doc.key, file);
                      }}
                      disabled={uploading === doc.key}
                      className="hidden"
                      id={`upload-${doc.key}`}
                    />
                    <label
                      htmlFor={`upload-${doc.key}`}
                      className="flex flex-col items-center cursor-pointer"
                    >
                      {uploading === doc.key ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <span className="text-sm text-gray-600 font-medium">
                            {status.status === 'rejected' ? 'Re-upload Document' : 'Upload Document'}
                          </span>
                          <span className="text-xs text-gray-500 mt-1">PNG, JPG or PDF (max 5MB)</span>
                        </>
                      )}
                    </label>
                  </div>
                )}

                {status.status === 'submitted' && (
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-2xl text-blue-600"></i>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Document Submitted</p>
                        <p className="text-sm text-gray-600">
                          Submitted on {new Date(status.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => window.open(status.file_url, '_blank')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        View
                      </button>
                    </div>
                  </div>
                )}

                {status.status === 'approved' && (
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-check-line text-2xl text-green-600"></i>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Document Approved</p>
                        <p className="text-sm text-gray-600">
                          Approved on {new Date(status.reviewed_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => window.open(status.file_url, '_blank')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        View
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Optional Documents */}
      {optionalDocs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Optional Documents</h2>
          <p className="text-sm text-gray-600 mb-6">These documents are recommended but not required</p>
          <div className="space-y-4">
            {optionalDocs.map((doc) => {
              const status = getDocumentStatus(doc.key);
              return (
                <div
                  key={doc.key}
                  className={`border-2 rounded-lg p-6 ${
                    status.status === 'approved' ? 'border-green-200 bg-green-50' :
                    status.status === 'submitted' ? 'border-blue-200 bg-blue-50' :
                    'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1">{doc.label}</h3>
                      <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
                      
                      {status.status === 'not_submitted' && (
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-bold rounded-full">
                          Optional
                        </span>
                      )}
                      {status.status === 'submitted' && (
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                          Under Review
                        </span>
                      )}
                      {status.status === 'approved' && (
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                          ✓ Approved
                        </span>
                      )}
                    </div>

                    {status.status === 'approved' && (
                      <i className="ri-check-circle-fill text-green-500 text-3xl"></i>
                    )}
                  </div>

                  {status.status === 'not_submitted' && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(doc.key, file);
                        }}
                        disabled={uploading === doc.key}
                        className="hidden"
                        id={`upload-${doc.key}`}
                      />
                      <label
                        htmlFor={`upload-${doc.key}`}
                        className="flex flex-col items-center cursor-pointer"
                      >
                        {uploading === doc.key ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                            <span className="text-sm text-gray-600">Uploading...</span>
                          </div>
                        ) : (
                          <>
                            <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                            <span className="text-sm text-gray-600 font-medium">Upload Document</span>
                            <span className="text-xs text-gray-500 mt-1">PNG, JPG or PDF (max 5MB)</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}

                  {(status.status === 'submitted' || status.status === 'approved') && (
                    <div className={`bg-white rounded-lg p-4 border ${status.status === 'approved' ? 'border-green-200' : 'border-blue-200'}`}>
                      <div className="flex items-center gap-3">
                        <i className={`${status.status === 'approved' ? 'ri-file-check-line text-green-600' : 'ri-file-text-line text-blue-600'} text-2xl`}></i>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {status.status === 'approved' ? 'Document Approved' : 'Document Submitted'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {status.status === 'approved' 
                              ? `Approved on ${new Date(status.reviewed_at).toLocaleDateString()}`
                              : `Submitted on ${new Date(status.submitted_at).toLocaleDateString()}`
                            }
                          </p>
                        </div>
                        <button
                          onClick={() => window.open(status.file_url, '_blank')}
                          className={`px-4 py-2 ${status.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors text-sm font-medium whitespace-nowrap cursor-pointer`}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
